import type { UIMessage } from "@ai-sdk/react";
import { useChat } from "@ai-sdk/react";
import { DefaultChatTransport, isToolUIPart } from "ai";
import { MessageSquare } from "lucide-react";
import { useCallback, useEffect, useMemo, useRef, useState } from "react";
import { toast } from "sonner";
import { ChatInput } from "./ChatInput";
import { ChatMessage } from "./ChatMessage";

interface ChatThreadProps {
  campaignId: string;
  onSideEffect: (action: Record<string, unknown> & { type: string }) => void;
}

const SUGGESTION_CHIPS = [
  "Summarize my results",
  "Score all locations",
  "Compare scoring profiles",
  "Which locations scored highest?",
];

export function ChatThread({ campaignId, onSideEffect }: ChatThreadProps) {
  const [input, setInput] = useState("");
  const scrollRef = useRef<HTMLDivElement>(null);
  const processedEffectsRef = useRef<Set<string>>(new Set());
  const onSideEffectRef = useRef(onSideEffect);
  onSideEffectRef.current = onSideEffect;

  // Track the pending message text so the transport can send it directly
  const pendingMessageRef = useRef("");

  const transport = useMemo(
    () =>
      new DefaultChatTransport({
        api: `/api/campaigns/${campaignId}/chat`,
        prepareSendMessagesRequest: async () => {
          return { body: { message: pendingMessageRef.current } };
        },
      }),
    [campaignId],
  );

  const { messages, sendMessage, status, setMessages, error } = useChat({
    id: `campaign-${campaignId}`,
    transport,
    onError: (err) => {
      toast.error(err.message || "Failed to send message");
    },
  });

  const isLoading = status === "streaming" || status === "submitted";

  // Load chat history on mount
  useEffect(() => {
    let mounted = true;
    processedEffectsRef.current.clear();

    async function loadHistory() {
      try {
        const res = await fetch(`/api/campaigns/${campaignId}/chat`);
        if (!res.ok) return;
        const data = await res.json();
        if (!mounted || !data.messages?.length) return;

        // Convert DB messages to UIMessage format
        const uiMessages: UIMessage[] = [];
        for (const msg of data.messages) {
          if (msg.role === "tool") continue; // tool messages are inline in assistant parts
          const parts: UIMessage["parts"] = [];
          if (msg.content) {
            parts.push({ type: "text" as const, text: msg.content });
          }
          // Add tool call parts for assistant messages
          if (msg.role === "assistant" && msg.toolCalls?.length) {
            for (const tc of msg.toolCalls) {
              parts.push({
                type: `tool-${tc.toolName}`,
                toolCallId: tc.toolCallId || crypto.randomUUID(),
                toolName: tc.toolName,
                state: "output-available",
                input: tc.args || {},
                output: tc.result || {},
              } as any);
            }
          }
          if (parts.length > 0) {
            uiMessages.push({
              id: msg.id,
              role: msg.role === "user" ? "user" : "assistant",
              parts,
            });
          }
        }
        if (uiMessages.length > 0) {
          setMessages(uiMessages);
        }
      } catch {
        // Silently fail — empty chat is fine
      }
    }

    loadHistory();
    return () => {
      mounted = false;
    };
  }, [campaignId, setMessages]);

  // Watch for side effects from tool invocations
  useEffect(() => {
    for (const msg of messages) {
      if (msg.role !== "assistant") continue;
      for (const part of msg.parts ?? []) {
        if (
          isToolUIPart(part) &&
          part.state === "output-available" &&
          (part as any).output?.sideEffect
        ) {
          const effectKey = `${msg.id}-${part.toolCallId}`;
          if (!processedEffectsRef.current.has(effectKey)) {
            processedEffectsRef.current.add(effectKey);
            onSideEffectRef.current((part as any).output.sideEffect);
          }
        }
      }
    }
  }, [messages]);

  // Auto-scroll to bottom when messages change
  // biome-ignore lint/correctness/useExhaustiveDependencies: messages triggers scroll on new content
  useEffect(() => {
    const el = scrollRef.current;
    if (el) {
      el.scrollTo({ top: el.scrollHeight, behavior: "smooth" });
    }
  }, [messages]);

  const handleSubmit = useCallback(() => {
    const text = input.trim();
    if (!text || isLoading) return;
    setInput("");
    pendingMessageRef.current = text;
    sendMessage({ text });
  }, [input, isLoading, sendMessage]);

  const handleChipClick = useCallback(
    (text: string) => {
      if (isLoading) return;
      pendingMessageRef.current = text;
      sendMessage({ text });
    },
    [isLoading, sendMessage],
  );

  return (
    <div className="flex flex-1 flex-col overflow-hidden">
      {/* Messages area */}
      <div
        ref={scrollRef}
        className="flex-1 overflow-y-auto px-3 py-3 space-y-3 mx-auto w-full max-w-[1024px]"
      >
        {messages.length === 0 && !isLoading && (
          <div className="flex flex-col items-center justify-center gap-3 py-8 text-center">
            <div className="flex h-10 w-10 items-center justify-center rounded-xl bg-muted">
              <MessageSquare className="h-5 w-5 text-muted-foreground" />
            </div>
            <p className="text-xs text-muted-foreground px-2">
              Start a conversation about your campaign locations
            </p>
            <div className="flex flex-wrap gap-1.5 justify-center px-2">
              {SUGGESTION_CHIPS.map((chip) => (
                <button
                  key={chip}
                  type="button"
                  onClick={() => handleChipClick(chip)}
                  className="rounded-full border border-border bg-background px-3 py-1 text-[11px] text-muted-foreground hover:bg-muted transition-colors"
                >
                  {chip}
                </button>
              ))}
            </div>
          </div>
        )}

        {messages.map((msg) => (
          <ChatMessage key={msg.id} message={msg} />
        ))}

        {error && (
          <div className="rounded-lg border border-destructive/50 bg-destructive/10 px-3 py-2 text-xs text-destructive">
            {error.message}
          </div>
        )}
      </div>

      {/* Input */}
      <div className="mx-auto w-full max-w-[1024px]">
        <ChatInput
          value={input}
          onChange={setInput}
          onSubmit={handleSubmit}
          isLoading={isLoading}
        />
      </div>
    </div>
  );
}
